'use client';

import React from 'react';
import { Button, ButtonProps, styled } from '@mui/material';

export interface StyledButtonProps extends ButtonProps {
    component?: React.ElementType;
    href?: string;
    target?: string;
}

function ArrowGraphic(svgProps: React.SVGProps<SVGSVGElement>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={100}
            height={54}
            className="svg-icon"
            {...svgProps}
        >
            <path stroke="currentColor" d="M0 27h70" strokeWidth={3} />
            <circle cx={60} cy={27} r={24} fill="transparent" stroke="currentColor" strokeWidth={2} />
            <path stroke="currentColor" d="m56 15 14 12L56 39l14-12" strokeWidth={3} strokeLinejoin="round" />
        </svg>
    );
}

export default function StyledButton(props: StyledButtonProps) {
    const { children, ...otherProps } = props;

    return (
        <AnimatedButton {...otherProps}>
            {children}
            <ArrowGraphic />
        </AnimatedButton>
    );
}

const AnimatedButton = styled(Button)(({ theme, color }) => ({
    ...theme.typography.button,
    fontWeight: 900,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: 'fit-content',
    padding: 0,
    transition: 'all 0.3s ease',
    color: color === 'secondary'
        ? theme.palette.secondary.main
        : theme.palette.mode === 'light'
            ? theme.palette.primary.dark
            : theme.palette.primary.main,
    textTransform: 'uppercase',
    '&:hover': {
        backgroundColor: 'transparent',
        color: color === 'secondary'
            ? theme.palette.secondary.main
            : theme.palette.mode === 'light'
                ? theme.palette.primary.dark
                : theme.palette.primary.main,
    },
    '& .svg-icon': {
        scale: 0.7,
        boxSizing: 'border-box', // Prevent stroke width from affecting layout
        transition: 'transform 0.3s ease',
    },
    '&:hover .svg-icon': {
        transform: 'translateX(2px)',
    },
    '& .svg-icon circle': {
        transition: 'stroke-width 0.3s ease',
        vectorEffect: 'non-scaling-stroke', // Prevent scaling effect
    },
    '&:hover .svg-icon circle': {
        strokeWidth: 4, // Increase stroke width on hover
    },
    '& .svg-icon path': {
        strokeLinejoin: 'round',
        vectorEffect: 'non-scaling-stroke', // Prevent scaling effect
    },
    '& .svg-icon path:nth-of-type(2)': {
        transition: 'stroke-width 0.3s ease',
    },
    '&:hover .svg-icon path:nth-of-type(2)': {
        strokeWidth: 4, // Increase stroke width on hover
    },
    '& .svg-icon path:first-of-type': {
        strokeDasharray: 70, // Total length of the path
        strokeDashoffset: 0, // Fully visible initially
        transition: 'stroke-dashoffset 0.3s ease',
    },
    '&:hover .svg-icon path:first-of-type': {
        strokeDashoffset: -70, // Disappears to the right on hover
    },
}));
